export const dailyAssistant = {
  seo: {
    title: "Always-on AI assistant — Bespire",
    description:
      "Daily brief, task management, proactive nudges, weekly and monthly reports. Bespire AI works with you every day.",
  },
  hero: {
    badge: "Always-on AI assistant",
    title: "An assistant that works with you every single day",
    subtitle:
      "Not a chatbot you have to prompt. Bespire AI preps you in the morning, pings you during the day if something needs attention, wraps up in the evening — and comes back weekly / monthly with concrete suggestions.",
    ctaTrial: "Try it free for 3 days",
    ctaPricing: "See pricing →",
    phoneBrief: {
      title: "This morning",
      time: "Wed, Jun 24, 2026 · 07:00",
      greeting: "Good morning, Kata! 5 bookings today, one open slot mid-morning. Here's what to watch:",
      notifications: [
        { icon: "Calendar", tag: "Day", text: "5 bookings, projected revenue €175" },
        { icon: "Clock", tag: "10:30", text: "Open slot — 4 waitlisted, should I message them?" },
        { icon: "Star", tag: "New", text: "3 reviews came in, replies drafted" },
        { icon: "Bell", tag: "Reminder", text: "2 overdue invoices, send reminder?" },
        { icon: "MessageCircle", tag: "Campaign", text: "Win-back for 12 dormant customers scheduled for 07:30" },
      ],
      actionApprove: "Approve all",
      actionOpen: "Review",
    },
  },
  timeline: {
    title: "A day with Bespire",
    subtitle: "Not extra work — it follows the rhythm of your day. Morning to evening, all on one line.",
    events: [
      {
        time: "07:00",
        icon: "Sunrise",
        title: "Daily brief",
        note: "Bookings, revenue, 3 priorities — pinged to your phone.",
      },
      {
        time: "09:30",
        icon: "Calendar",
        title: "New booking",
        note: "Automatic confirmation, calendar updated.",
      },
      {
        time: "10:30",
        icon: "AlertCircle",
        title: "Cancellation → gap fill",
        note: "Message goes to 4 waitlisted people in order. 1 yes within 3 minutes.",
      },
      {
        time: "13:00",
        icon: "Sparkles",
        title: "Proactive nudge",
        note: "Group class isn't on Instagram yet — should I post the weekly template?",
      },
      {
        time: "16:45",
        icon: "Star",
        title: "New review",
        note: "5-star from Andrea T. — reply drafted, waiting for approval.",
      },
      {
        time: "18:00",
        icon: "Moon",
        title: "End-of-day wrap",
        note: "What worked, what didn't, tomorrow's first 3 things — you can log off.",
      },
    ],
  },
  moments: {
    title: "Proactive moments — when Bespire actually earns its keep",
    subtitle: "It doesn't wait to be asked. It speaks up before something becomes a problem.",
    items: [
      {
        icon: "Clock",
        situation: "Open slot on the calendar",
        aiMessage: "You have a 90-min gap at 13:00. Should I send a win-back message to your 6 most recent dormant customers?",
        action: "Approve send",
      },
      {
        icon: "Gift",
        situation: "VIP birthday",
        aiMessage: "Zoltán Kovács is celebrating tomorrow. He's been a regular for 5 years — should I send a personal note and 20% off?",
        action: "Approve message + gift",
      },
      {
        icon: "AlertTriangle",
        situation: "Negative review",
        aiMessage: "3-star review from Balázs — 15-minute wait complaint. I drafted an empathetic reply + discount coupon. Approve?",
        action: "Approve reply",
      },
      {
        icon: "Bell",
        situation: "Overdue invoice",
        aiMessage: "Anna Tóth's €115 invoice is 7 days overdue. A polite reminder is ready. Send?",
        action: "Approve reminder",
      },
    ],
  },
  reports: {
    title: "Weekly & monthly reports — numbers, plus what to do",
    subtitle: "Bespire doesn't leave the analysis for your next accountant meeting.",
    weekly: {
      badge: "Weekly report",
      title: "Week 26 · Sunday 18:00",
      kpis: [
        { label: "Revenue", value: "€1,050", delta: "+8%" },
        { label: "Bookings", value: "47", delta: "+3" },
        { label: "New clients", value: "6", delta: "+2" },
        { label: "Reviews", value: "5 (avg 4.9)", delta: "+2" },
      ],
      insightTitle: "Key insight",
      insight: "Friday evening is your weekly peak, but Wednesday mornings have been a gap for 3 weeks. Group class there?",
      action: "Draft a plan",
    },
    monthly: {
      badge: "Monthly report",
      title: "June · trends & suggestions",
      kpis: [
        { label: "Monthly revenue", value: "€4,730", delta: "+12%" },
        { label: "Active clients", value: "84", delta: "+6" },
        { label: "New clients", value: "18", delta: "+5" },
        { label: "Dormant", value: "23", delta: "attention" },
      ],
      insightTitle: "Key insight",
      insight: "3-month trend: evening slots stay full, morning peak has softened. Let's build a July strategy around it.",
      action: "Open monthly plan",
    },
  },
  chat: {
    title: "A whole morning conversation — end to end",
    subtitle: "Here's what a Wednesday morning looks like when you open Bespire.",
    chat: {
      title: "Daily assistant",
      online: "Online",
      messages: [
        { role: "assistant" as const, content: "Good morning! ☀️ Here's your brief:", list: [
          "📅 5 bookings (2 new, 3 regulars)",
          "💰 Projected revenue: €175",
          "⚠️ 10:30 slot open — win-back suggestion ready",
        ] },
        { role: "user" as const, content: "What should I focus on today?" },
        { role: "assistant" as const, content: "3 things, in order:", list: [
          "1. Anna Kovács hasn't booked in 3 weeks — send a message?",
          "2. The group class isn't on Instagram yet",
          "3. 2 overdue invoices — reminder can go out",
        ] },
        { role: "user" as const, content: "Do all three." },
        { role: "assistant" as const, content: "Done. I'll update you during the day if anyone replies. Wrap-up at 18:00. 📊" },
        { role: "assistant" as const, content: "🔔 Update — 10:32: one of your waitlisted (Éva Kiss) confirmed the 10:30 slot. Filled." },
        { role: "user" as const, content: "Nice. What should I do until 14:00? I have 90 minutes." },
        { role: "assistant" as const, content: "Two options:", list: [
          "📷 I can draft a 3-post Instagram series for the group class — 15 minutes",
          "☕ Or take a break — the day is fine, everything runs in the background",
        ] },
      ],
    },
  },
  quietHours: {
    text: "Goes quiet on weekends, holidays, or any quiet hours you set. You decide when it speaks up.",
  },
  crossLinks: {
    title: "The daily assistant sees everything",
    subtitle: "Not a separate feature — the same AI handles your brand, bookings, marketing and customers.",
  },
  cta: {
    title: "Stop working alone",
    subtitle: "3-day free trial, no card required.",
    primary: "Try it free for 3 days",
    secondary: "See pricing",
  },
};
